// Setup > Fee schedules: the named schedules behind PracticeSuite's "Export All"
// workbook (Vortex, 2026-10-18) and the reviewable schedule-to-payer mapping.
//
// Three pieces, all against /api/practices/<id>/fee-schedule:
//   ExportUploadForm   the loader's "PracticeSuite Export All" source: pick the .xlsx,
//                      see the dry run per schedule (and what was skipped and why),
//                      confirm to load. The rate is the workbook's "Billed Amt."
//                      column: the biller stores each payer's CONTRACTED rate there.
//   PayerGroupsCard    which payers each schedule applies to (payer code, exact name,
//                      a word in the name, or a payer record), the payers seen on the
//                      practice's claims with the schedule each resolves to, add and
//                      delete. A payer no row names gets no schedule.
//   ValidationCard     the schedules against the ERAs: per schedule and CPT, the rate
//                      beside what the payer's 835s allowed; match / over / under /
//                      no ERA yet / no schedule row. ERA-first: the recorded allowed is
//                      always the price, the schedule only sets Expected.
//
// Schedule names, codes, rates, dates, payer codes and names: nothing here is about a
// patient. The backend is the boundary (admin + practice access).

import React, { useCallback, useEffect, useRef, useState } from "react";

const API_URL = process.env.REACT_APP_API_URL || "";

const base = (practiceId) =>
  `${API_URL}/api/practices/${practiceId}/fee-schedule`;

export const MATCH_KIND_LABELS = {
  payer_code: "payer code (exact)",
  payer_name: "payer name (exact)",
  name_contains: "payer name contains",
  payer_record: "payer record",
};

export const MAPPING_SOURCE_LABELS = {
  grid: "added here",
  "brief-2026-10-18": "seeded from the brief (2026-10-18)",
};

export const STATUS_CLASS = {
  match: "ok",
  over: "over",
  under: "under",
  no_era: "none",
  no_rate: "none",
};

async function request(target, options, fallback) {
  const response = await fetch(target, { credentials: "include", ...options });
  const body = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error((body && body.error) || fallback);
  }
  if (!body || typeof body !== "object") {
    throw new Error("The response could not be read. Try again.");
  }
  return body;
}

export function uploadExport(practiceId, { file, effectiveFrom, write }) {
  const form = new FormData();
  form.append("file", file);
  if (effectiveFrom) form.append("effective_from", effectiveFrom);
  form.append("write", write ? "true" : "false");
  return request(
    `${base(practiceId)}/export-upload`,
    { method: "POST", body: form },
    write ? "Could not load the export" : "Could not read the export",
  );
}

export function fetchPayerGroups(practiceId) {
  return request(
    `${base(practiceId)}/payer-groups`,
    {},
    "Could not load the schedule mapping",
  );
}

export function addPayerGroup(practiceId, body) {
  return request(
    `${base(practiceId)}/payer-groups`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
    "Could not add the mapping row",
  );
}

export function deletePayerGroup(practiceId, rowId) {
  return request(
    `${base(practiceId)}/payer-groups/${rowId}`,
    { method: "DELETE" },
    "Could not delete the mapping row",
  );
}

export function fetchValidation(practiceId) {
  return request(
    `${base(practiceId)}/validation`,
    {},
    "Could not load the validation report",
  );
}

export function describeExport(report) {
  if (!report) return "";
  const schedules = Array.isArray(report.schedules) ? report.schedules : [];
  const parts = [
    `${report.rows_read} rows read`,
    `${schedules.length} payer schedule${schedules.length === 1 ? "" : "s"}`,
    `${report.new} new`,
    `${report.changed} changed`,
    `${report.unchanged} unchanged`,
  ];
  return parts.join(", ");
}

export function describeSkipped(report) {
  if (!report) return "";
  const bits = [];
  if (report.charge_master) {
    bits.push(
      `${report.charge_master.name} (${report.charge_master.rows} rows) is the charge master, not a contract: not loaded`,
    );
  }
  (report.not_insurance || []).forEach((s) => {
    bits.push(
      `${s.name} (${s.rows} rows) is not an insurance contract: not loaded`,
    );
  });
  const skipped = report.skipped || {};
  if (skipped.zero_rate) {
    bits.push(
      `${skipped.zero_rate} row${skipped.zero_rate === 1 ? "" : "s"} with a zero rate dropped`,
    );
  }
  if (skipped.non_cpt && skipped.non_cpt.count) {
    bits.push(
      `${skipped.non_cpt.count} non-CPT code${skipped.non_cpt.count === 1 ? "" : "s"} dropped (${skipped.non_cpt.codes.join(", ")})`,
    );
  }
  return bits.join("; ");
}

function formatDay(value) {
  if (!value) return "";
  const [y, m, d] = value.split("-");
  return `${m}/${d}/${y}`;
}

function money(value) {
  if (value === null || value === undefined || value === "") return "—";
  const n = Number(value);
  return Number.isNaN(n) ? String(value) : `$${n.toFixed(2)}`;
}

export function ExportUploadForm({
  practiceId,
  busy,
  setBusy,
  setError,
  setMessage,
  onLoaded,
}) {
  const [from, setFrom] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const fileInput = useRef(null);

  const run = async (write) => {
    if (busy || !file) return;
    setBusy(true);
    setError(null);
    try {
      const report = await uploadExport(practiceId, {
        file,
        effectiveFrom: from,
        write,
      });
      if (write) {
        setMessage(
          `Loaded the PracticeSuite export: ${describeExport(report)}.`,
        );
        setFile(null);
        setPreview(null);
        if (fileInput.current) fileInput.current.value = "";
        if (onLoaded) onLoaded(report);
      } else {
        setPreview(report);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const pending = preview ? preview.new + preview.changed : 0;
  return (
    <div data-testid="fee-schedule-export-form">
      <p className="fs-muted">
        PracticeSuite &gt; Advanced Setup &gt; Fee Schedule &gt; Export All: one
        workbook, every schedule. The rate is the <strong>Billed Amt.</strong>{" "}
        column (the biller keeps each payer&apos;s contracted rate there;
        Allowed Amt. is 0 everywhere). Standard, Private Pay and Personal Injury
        are not insurance contracts and are not loaded; zero rates and non-CPT
        codes are dropped. A schedule prices a claim only once it is mapped to
        the claim&apos;s payer below.
      </p>
      <div className="fs-form">
        <label>
          Effective from (rows without a Start Date)
          <input
            type="date"
            value={from}
            onChange={(e) => {
              setFrom(e.target.value);
              setPreview(null);
            }}
          />
        </label>
        <label>
          Export All workbook (.xlsx)
          <input
            ref={fileInput}
            type="file"
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            aria-label="Export All workbook"
            onChange={(e) => {
              setFile(
                e.target.files && e.target.files[0] ? e.target.files[0] : null,
              );
              setPreview(null);
            }}
          />
        </label>
        <div className="fs-form-actions">
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            disabled={busy || !file}
            onClick={() => run(false)}
          >
            {busy && !preview ? "Reading…" : "Preview"}
          </button>
          {preview ? (
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled={busy || pending === 0}
              onClick={() => run(true)}
              data-testid="fee-schedule-export-load"
            >
              {busy
                ? "Loading…"
                : `Load ${pending} rate${pending === 1 ? "" : "s"}`}
            </button>
          ) : null}
        </div>
      </div>
      {preview ? (
        <div className="fs-preview" data-testid="fee-schedule-export-preview">
          <div>
            Dry run: {describeExport(preview)}. Rows without a Start Date take
            effect {formatDay(preview.effective_from)}.
          </div>
          {describeSkipped(preview) ? (
            <div className="fs-muted">{describeSkipped(preview)}.</div>
          ) : null}
          <table className="fs-table">
            <thead>
              <tr>
                <th>Schedule</th>
                <th className="n">Rows</th>
                <th className="n">New</th>
                <th className="n">Changed</th>
                <th className="n">Unchanged</th>
                <th>Mapped to a payer</th>
              </tr>
            </thead>
            <tbody>
              {(preview.schedules || []).map((s) => (
                <tr key={s.name} data-testid="fee-schedule-export-schedule">
                  <td>{s.name}</td>
                  <td className="n">{s.rows}</td>
                  <td className="n">{s.new}</td>
                  <td className="n">{s.changed}</td>
                  <td className="n">{s.unchanged}</td>
                  <td>{s.mapped ? "yes" : "not yet"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}

const EMPTY_MAPPING = {
  schedule_name: "",
  match_kind: "payer_code",
  match_value: "",
  practice_payer_id: "",
  notes: "",
};

export function PayerGroupsCard({
  practiceId,
  payers,
  readOnly,
  reloadKey,
  onError,
}) {
  const [data, setData] = useState(null);
  const [localError, setLocalError] = useState(null);
  // The page shows one error strip for every card when it passes a setter.
  const setError = onError || setLocalError;
  const error = onError ? null : localError;
  const [message, setMessage] = useState(null);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState(EMPTY_MAPPING);
  const current = useRef(practiceId);
  current.current = practiceId;

  const load = useCallback(() => {
    if (!practiceId) return () => {};
    let cancelled = false;
    fetchPayerGroups(practiceId)
      .then((body) => {
        if (!cancelled && current.current === practiceId) setData(body);
      })
      .catch((e) => {
        if (!cancelled) setError(e.message);
      });
    return () => {
      cancelled = true;
    };
  }, [practiceId]);

  useEffect(() => {
    setData(null);
    setError(null);
    setMessage(null);
    setDraft(EMPTY_MAPPING);
    return load();
  }, [load, reloadKey]);

  const add = async (body) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const row = await addPayerGroup(practiceId, body);
      setMessage(
        `Mapped ${row.match_value || "the payer"} to ${row.schedule_name}.`,
      );
      setDraft({ ...EMPTY_MAPPING, schedule_name: body.schedule_name || "" });
      load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (row) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await deletePayerGroup(practiceId, row.id);
      setMessage(
        `Removed ${row.match_value || "the payer"} from ${row.schedule_name}.`,
      );
      load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const summary = (data && data.summary) || {};
  const schedules = Array.isArray(summary.schedules) ? summary.schedules : [];
  const names = Array.isArray(data && data.schedule_names)
    ? data.schedule_names
    : [];
  const claimPayers = Array.isArray(data && data.claim_payers)
    ? data.claim_payers
    : [];
  const unavailable = data && data.available === false;
  const canEdit = !readOnly && !unavailable;

  return (
    <div className="fs-card" data-testid="fee-schedule-groups">
      <h2>
        PracticeSuite schedules and their payers{" "}
        <span className={`fs-badge ${schedules.length ? "on" : "off"}`}>
          {schedules.length
            ? `${schedules.length} schedule${schedules.length === 1 ? "" : "s"} · ${summary.row_count || 0} rows`
            : "none"}
        </span>
      </h2>
      <p className="fs-muted">
        A schedule applies to a group of payers (AETNA to Aetna and Meritain,
        its TPA). A payer is recognised on a claim by PracticeSuite&apos;s payer
        code, its exact name, a word its name contains, or one of this
        practice&apos;s payer records; a payer no row names gets no schedule and
        its claims price Expected at the allowed, as before. Rows seeded from
        the brief are here to be corrected.
      </p>
      {error ? (
        <div className="fs-message error" role="alert">
          <span>{error}</span>
          <button
            type="button"
            className="message-close"
            aria-label="Dismiss"
            onClick={() => setError(null)}
          >
            ×
          </button>
        </div>
      ) : null}
      {message ? (
        <div className="fs-message ok" role="status">
          <span>{message}</span>
          <button
            type="button"
            className="message-close"
            aria-label="Dismiss"
            onClick={() => setMessage(null)}
          >
            ×
          </button>
        </div>
      ) : null}
      {unavailable ? (
        <div className="fs-pending" data-testid="fee-schedule-groups-pending">
          Named schedules are unavailable until migration {data.migration} has
          run; the Waterfall prices Expected from the per-payer rows alone until
          then.
        </div>
      ) : null}

      {schedules.length ? (
        <table className="fs-table">
          <thead>
            <tr>
              <th>Schedule</th>
              <th className="n">Rows</th>
              <th className="n">Codes</th>
              <th className="n">In force today</th>
              <th>Applies to</th>
            </tr>
          </thead>
          <tbody>
            {schedules.map((s) => (
              <tr
                key={s.name}
                data-testid="fee-schedule-group-row"
                className={s.loaded ? "" : "muted"}
              >
                <td>
                  {s.name}
                  {!s.loaded ? (
                    <span className="fs-muted"> (mapped, not loaded)</span>
                  ) : null}
                </td>
                <td className="n">{s.rows}</td>
                <td className="n">{s.codes}</td>
                <td className="n">{s.in_force}</td>
                <td>
                  {s.payers && s.payers.length ? (
                    <ul className="fs-chips">
                      {s.payers.map((m) => (
                        <li key={m.id} data-testid="fee-schedule-group-payer">
                          <span
                            title={MAPPING_SOURCE_LABELS[m.source] || m.source}
                          >
                            {MATCH_KIND_LABELS[m.match_kind] || m.match_kind}:{" "}
                            <strong>{m.match_value}</strong>
                            {m.notes ? ` — ${m.notes}` : ""}
                          </span>
                          {canEdit ? (
                            <button
                              type="button"
                              className="btn btn-secondary btn-sm"
                              onClick={() => remove(m)}
                              disabled={busy}
                              data-testid="fee-schedule-group-unmap"
                            >
                              remove
                            </button>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <span
                      className="fs-warn"
                      data-testid="fee-schedule-group-unmapped"
                    >
                      no payer yet: prices nothing
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : data && !unavailable ? (
        <p className="fs-muted">
          No PracticeSuite schedule loaded or mapped yet. Load the Export All
          workbook above, then say which payers each schedule is for.
        </p>
      ) : null}

      {canEdit ? (
        <div className="fs-form" data-testid="fee-schedule-group-add">
          <label>
            Schedule
            <input
              type="text"
              list="fs-schedule-names"
              value={draft.schedule_name}
              placeholder="e.g. AETNA"
              aria-label="Schedule name"
              onChange={(e) =>
                setDraft({ ...draft, schedule_name: e.target.value })
              }
            />
            <datalist id="fs-schedule-names">
              {names.map((n) => (
                <option key={n} value={n} />
              ))}
            </datalist>
          </label>
          <label>
            Recognise the payer by
            <select
              aria-label="Match kind"
              value={draft.match_kind}
              onChange={(e) =>
                setDraft({ ...draft, match_kind: e.target.value })
              }
            >
              {Object.entries(MATCH_KIND_LABELS).map(([k, label]) => (
                <option key={k} value={k}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          {draft.match_kind === "payer_record" ? (
            <label>
              Payer record
              <select
                aria-label="Payer record"
                value={draft.practice_payer_id}
                onChange={(e) =>
                  setDraft({ ...draft, practice_payer_id: e.target.value })
                }
              >
                <option value="">Select a payer…</option>
                {(payers || []).map((p) => (
                  <option key={p.id} value={String(p.id)}>
                    {p.payer_name}
                  </option>
                ))}
              </select>
            </label>
          ) : (
            <label>
              {draft.match_kind === "payer_code"
                ? "Payer code (PracticeSuite's payer id)"
                : draft.match_kind === "payer_name"
                  ? "Payer name, as the claims carry it"
                  : "Word the payer name contains"}
              <input
                type="text"
                aria-label="Match value"
                value={draft.match_value}
                onChange={(e) =>
                  setDraft({ ...draft, match_value: e.target.value })
                }
              />
            </label>
          )}
          <label>
            Note (optional)
            <input
              type="text"
              aria-label="Mapping note"
              value={draft.notes}
              onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
            />
          </label>
          <div className="fs-form-actions">
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled={
                busy ||
                !draft.schedule_name ||
                (draft.match_kind === "payer_record"
                  ? !draft.practice_payer_id
                  : !draft.match_value)
              }
              onClick={() => add(draft)}
              data-testid="fee-schedule-group-save"
            >
              Map the payer
            </button>
          </div>
        </div>
      ) : null}

      {claimPayers.length ? (
        <div data-testid="fee-schedule-claim-payers">
          <h3>Payers on this practice&apos;s claims</h3>
          <table className="fs-table">
            <thead>
              <tr>
                <th>Payer</th>
                <th>Code</th>
                <th className="n">Claims</th>
                <th>Schedule</th>
              </tr>
            </thead>
            <tbody>
              {claimPayers.map((p) => (
                <tr
                  key={`${p.practice_payer_id || ""}-${p.payer_code || ""}-${p.payer_name || ""}`}
                  data-testid="fee-schedule-claim-payer"
                  className={p.schedule ? "" : "muted"}
                >
                  <td>{p.payer_name || "(no name)"}</td>
                  <td>{p.payer_code || "—"}</td>
                  <td className="n">{p.claims}</td>
                  <td>
                    {p.schedule ? (
                      p.schedule
                    ) : canEdit && names.length ? (
                      <select
                        aria-label={`Schedule for ${p.payer_name || p.payer_code}`}
                        value=""
                        disabled={busy}
                        onChange={(e) => {
                          if (!e.target.value) return;
                          add({
                            schedule_name: e.target.value,
                            match_kind: p.payer_code
                              ? "payer_code"
                              : "payer_name",
                            match_value: p.payer_code || p.payer_name,
                          });
                        }}
                      >
                        <option value="">unmapped: Expected = Allowed</option>
                        {names.map((n) => (
                          <option key={n} value={n}>
                            map to {n}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span className="fs-muted">
                        unmapped: Expected = Allowed
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}

export function ValidationCard({ practiceId, reloadKey, onError }) {
  // The report prices the practice's ledger over a year, so it is computed on
  // request (the button) and again when the rates or the mapping change while open.
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [requested, setRequested] = useState(false);
  const [localError, setLocalError] = useState(null);
  const setError = onError || setLocalError;
  const error = onError ? null : localError;
  const current = useRef(practiceId);
  current.current = practiceId;

  useEffect(() => {
    setData(null);
    setRequested(false);
  }, [practiceId]);

  const load = useCallback(() => {
    if (!practiceId) return;
    setBusy(true);
    setError(null);
    fetchValidation(practiceId)
      .then((body) => {
        if (current.current === practiceId) setData(body);
      })
      .catch((e) => setError(e.message))
      .finally(() => setBusy(false));
  }, [practiceId, setError]);

  useEffect(() => {
    if (requested) load();
  }, [reloadKey]);

  const schedules = Array.isArray(data && data.schedules) ? data.schedules : [];
  const unmapped = Array.isArray(data && data.unmapped) ? data.unmapped : [];
  const labels = {};
  (Array.isArray(data && data.statuses) ? data.statuses : []).forEach((s) => {
    labels[s.key] = s.label;
  });
  if (data && data.available === false) return null;

  return (
    <div className="fs-card" data-testid="fee-schedule-validation">
      <h2>Schedules against the ERAs</h2>
      <p className="fs-muted">
        Per schedule and CPT: the loaded rate beside what the payer&apos;s 835s
        actually allowed for that payer group
        {data && data.window
          ? ` (claims with a date of service from ${formatDay(data.window.from)} to ${formatDay(data.window.to)})`
          : ""}
        . A match is within $0.01. &quot;Over&quot; means the payer allowed more
        than the schedule (the loaded rate may be stale); &quot;under&quot; is
        the underpaid-vs-contract gap the Waterfall shows as OA. The rule is
        ERA-first: the recorded allowed is always the price, the schedule only
        sets Expected.
      </p>
      {error ? (
        <div className="fs-message error" role="alert">
          <span>{error}</span>
          <button
            type="button"
            className="message-close"
            aria-label="Dismiss"
            onClick={() => setError(null)}
          >
            ×
          </button>
        </div>
      ) : null}
      <div className="fs-form-actions">
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          disabled={busy}
          onClick={() => {
            setRequested(true);
            load();
          }}
          data-testid="fee-schedule-validation-run"
        >
          {busy
            ? "Comparing…"
            : data
              ? "Compare again"
              : "Compare the schedules to the ERAs"}
        </button>
      </div>
      {data && data.empty ? (
        <p className="fs-muted" data-testid="fee-schedule-validation-empty">
          Nothing to compare yet: load a PracticeSuite export and map its
          schedules to payers first.
        </p>
      ) : null}
      {data && !data.empty && !schedules.length && !unmapped.length ? (
        <p className="fs-muted" data-testid="fee-schedule-validation-empty">
          No ERA-priced lines in the window and no loaded schedule to list.
        </p>
      ) : null}
      {schedules.map((s) => (
        <div key={s.name} data-testid="fee-schedule-validation-schedule">
          <h3>
            {s.name}{" "}
            <span className="fs-status-row">
              {Object.entries(s.counts || {})
                .filter(([, n]) => n > 0)
                .map(([k, n]) => (
                  <span
                    key={k}
                    className={`fs-status ${STATUS_CLASS[k] || ""}`}
                  >
                    {n} {labels[k] || k}
                  </span>
                ))}
            </span>
          </h3>
          <table className="fs-table">
            <thead>
              <tr>
                <th>CPT</th>
                <th className="n">Schedule rate</th>
                <th className="n">ERA allowed (median)</th>
                <th className="n">Low – high</th>
                <th className="n">Lines / claims</th>
                <th className="n">Difference</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {(s.rows || []).map((r) => (
                <tr key={r.cpt} data-testid="fee-schedule-validation-row">
                  <td>{r.cpt}</td>
                  <td className="n">
                    {money(r.rate)}
                    {r.rates && r.rates.length > 1 ? (
                      <span className="fs-muted"> (several)</span>
                    ) : null}
                  </td>
                  <td className="n">{money(r.era_median)}</td>
                  <td className="n">
                    {r.lines
                      ? `${money(r.era_low)} – ${money(r.era_high)}`
                      : "—"}
                  </td>
                  <td className="n">
                    {r.lines} / {r.claims}
                  </td>
                  <td className="n">
                    {r.difference === null || r.difference === undefined
                      ? "—"
                      : money(r.difference)}
                  </td>
                  <td>
                    <span
                      className={`fs-status ${STATUS_CLASS[r.status] || ""}`}
                    >
                      {labels[r.status] || r.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
      {unmapped.length ? (
        <p className="fs-muted" data-testid="fee-schedule-validation-unmapped">
          ERA-priced lines on payers no schedule names:{" "}
          {unmapped
            .map(
              (u) =>
                `${u.payer}${u.payer_code ? ` (${u.payer_code})` : ""}: ${u.lines} line${u.lines === 1 ? "" : "s"} on ${u.claims} claim${u.claims === 1 ? "" : "s"}`,
            )
            .join("; ")}
          . Map them above to compare.
        </p>
      ) : null}
    </div>
  );
}
